import {
  AuthorizationField,
  ContentTypeField,
  credentialsFieldValueFormat,
} from "@redotech/http/semantics";
import { Json } from "@redotech/json/json";
import { MultipartType } from "@redotech/media-type/multipart";
import { bearerCredentialsFormat } from "@redotech/oauth2/request";
import {
  EmailConsent,
  MessagingConsent,
  SMSConsent,
} from "@redotech/redo-model/messaging-consent";
import { ReturnAddress } from "@redotech/redo-model/return";
import { Address } from "@redotech/redo-model/return-flow";
import axios, { AxiosHeaders, AxiosResponse } from "axios";
import { REDO_API_URL } from "./config";

const TOKEN_KEY = "redo.portal_auth_token";

const client = () => {
  const widgetId = getWidgetId();
  if (!widgetId) {
    throw new Error("Missing ?widget_id");
  }
  const base = `${REDO_API_URL}/widgets/${widgetId}`;
  const headers = new AxiosHeaders();
  const client = axios.create({ baseURL: base, headers });
  return client;
};

const WIDGET_ID_REGEX = /^\/widget_id\/([^/]+).*/;

export const getWidgetId = () => {
  let widgetId =
    WIDGET_ID_REGEX.test(window.location.pathname) &&
    window.location.pathname.replace(WIDGET_ID_REGEX, "$1");
  if (!widgetId && widget_id !== "replace_me") {
    widgetId = widget_id;
  }
  return widgetId;
};

const authentication = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return {};
  }
  return {
    [String(AuthorizationField.name)]: credentialsFieldValueFormat.write(
      bearerCredentialsFormat.write(token),
    ),
  };
};

export const getOrders = (params) => {
  return client().get("orders", { headers: authentication(), params });
};

export const getOrderDiscountByOrderId = (id) => {
  return client().get(`orders/${id}/discount`, { headers: authentication() });
};

export const createOrderDiscountByOrderId = (id) => {
  return client().post(`orders/${id}/discount`, { headers: authentication() });
};

export const getOrderById = (id, includeUnfulfilled = false) => {
  return client().get(
    `orders/${id}${includeUnfulfilled ? "?includeUnfulfilled=true" : ""}`,
    { headers: authentication() },
  );
};

export const getReturn = (id) => {
  return client().get(`returns/${id}`, { headers: authentication() });
};

export const updateReturnNewOrderAddress = (id: string, address: Address) => {
  return client().put(`returns/${id}/newOrderAddress`, {
    headers: authentication(),
    params: {
      address: {
        name: address.name,
        address1: address.street1,
        address2: address.street2,
        city: address.city,
        province: address.state,
        zip: address.zip,
        country: address.country,
        phone: address.phone,
        email: address.email,
        country_code: address.country,
      },
    },
  });
};

export const getCurrencyExchangeRates = () => {
  return client().get("currency-conversion-rates", {
    headers: authentication(),
  });
};

export const getCarrierLocations = (
  carrier: string,
  params: { zip: string; country?: string },
) => {
  return client().get(`/carriers/${carrier}/locations`, {
    params,
    headers: authentication(),
  });
};

export type CreateAuthRequest =
  | {
      type: "order";
      email: string;
      orderNumber: string;
      isGift: boolean;
      emailOrZip: string;
    }
  | { type: "email"; token: string };

export interface CreateAuthResponse {
  token: string;
  customer: { email: string; name: string };
  orderID: string;
}

/**
 * POST /widgets/:id/customer-auth
 */
export const createAuth = async (
  request: CreateAuthRequest,
): Promise<CreateAuthResponse> => {
  let body: Json;
  switch (request.type) {
    case "email":
      body = { token: request.token };
      break;
    case "order":
      body = {
        email: request.email,
        orderNumber: request.orderNumber,
        isGift: request.isGift,
        emailOrZip: request.emailOrZip,
      };
      break;
  }
  const response = await client().post("customer-auth", body, {
    params: { type: request.type },
  });
  return response.data;
};

export interface SendAuthEmailRequest {
  email: string;
  url: string;
}

export interface SendAuthEmailResponse {}

/**
 * POST /widgets/:id/customer-auth/email
 */
export const sendAuthEmail = async (
  request: SendAuthEmailRequest,
): Promise<SendAuthEmailResponse> => {
  const response = await client().post("customer-auth/email", request);
  return response.data;
};

let settingsPromise: Promise<AxiosResponse<any, any>> | null = null;

export const getSettings = () => {
  if (!settingsPromise) {
    settingsPromise = client()
      .get("")
      .catch((e) => {
        settingsPromise = null;
        throw e;
      });
  }
  return settingsPromise;
};

export const getProduct = (id) => {
  return client().get(`products/${id}`, { headers: authentication() });
};

export const getProductTaxes = (params: {
  products: {
    variantId: string;
    price: string;
  }[];
  shippingAddress: ReturnAddress;
  orderId: string;
}) => {
  return client().post("products/taxes", { headers: authentication(), params });
};

export const getMessagingConsent = async (
  customerId: string,
): Promise<MessagingConsent> => {
  const res = await client().get(`customer-consent/${customerId}`, {
    headers: authentication(),
  });
  return res.data;
};

export const updateMessagingConsent = async (
  customerId: string,
  {
    smsMarketingConsent,
    emailMarketingConsent,
    smsTransactionalConsent,
    emailTransactionalConsent,
  }: {
    smsMarketingConsent?: SMSConsent;
    emailMarketingConsent?: EmailConsent;
    emailTransactionalConsent?: EmailConsent;
    smsTransactionalConsent?: SMSConsent;
  },
): Promise<MessagingConsent> => {
  const res = await client().post(`customer-consent/${customerId}`, {
    headers: authentication(),
    body: {
      smsMarketingConsent,
      emailMarketingConsent,
      smsTransactionalConsent,
      emailTransactionalConsent,
    },
  });
  return res.data;
};

export const getShopifyOrder = (shopifyOrderId: number) => {
  return client().get(`shopify/order/${shopifyOrderId}`, {
    headers: authentication(),
  });
};

export const getProducts = () => {
  return client().get("shopify/products", { headers: authentication() });
};

export const getProductsByTags = (tags: string) => {
  return client().get(`shopify/products?tags=${tags}`, {
    headers: authentication(),
  });
};

export const getCollectionProducts = (collectionId: number) => {
  return client().get(`shopify/collections/${collectionId}/products`, {
    headers: authentication(),
  });
};

export const getLocations = () => {
  return client().get("shopify/locations", { headers: authentication() });
};

export const getRecommendations = (productId: number) => {
  return client().get(`shopify/recommendations/${productId}`, {
    headers: authentication(),
  });
};

export const searchProducts = (searchQuery: string) => {
  return client().get(`shopify/search/${searchQuery}`, {
    headers: authentication(),
  });
};

export const getShippingFee = (params) => {
  return client().put("returns/fee", { headers: authentication(), params });
};

export const getAutomations = () => {
  return client().get("automations");
};

export const uploadImages = (params) => {
  return client().post("upload", params, {
    headers: {
      ...authentication(),
      [String(ContentTypeField.name)]: String(
        MultipartType.subtype("form-data"),
      ),
    },
  });
};

export const submitReturn = (params) => {
  return client().post("returns/submit", { headers: authentication(), params });
};

export const createCart = (params) => {
  return client().post("cart/create", { headers: authentication(), params });
};

export const getCart = (params) => {
  return client().post("cart/get", { headers: authentication(), params });
};

export const addItemToCart = (params) => {
  return client().post("cart/add", { headers: authentication(), params });
};

export const removeItemFromCart = (params) => {
  return client().post("cart/remove", { headers: authentication(), params });
};

export const updateCartItemQuantity = (params: {
  storeUrl: string;
  itemId: string;
  quantity: number;
  cartId: string;
}) => {
  return client().post("cart/updateQuantity", {
    headers: authentication(),
    params,
  });
};

export const addDiscountCodesToCart = (params) => {
  return client().post("cart/discounts", { headers: authentication(), params });
};

export const recreateExpiredDiscountCodes = (params: {
  codes: string[];
  storeUrl: string;
}) => {
  return client().post("recreate-discount-codes", {
    headers: authentication(),
    params,
  });
};

export const getCollectionsForProduct = (productId: string) => {
  return client().get(`/shopify/products/${productId}/collections`, {
    headers: authentication(),
  });
};

export const completeExchange = (token: string) => {
  return client().get(`/returns/completeExchange?token=${token}`);
};

export const verifyAddress = async (address: Address) => {
  const response = await client().post("returns/address/verify", address, {
    headers: authentication(),
  });
  return response.data;
};

export const getProductsBySkus = (skus: string[]) => {
  return client().get(`/products?skus=${skus.join(",")}`);
};

export const createAstraUserIntent = (params) => {
  return client().post("/astra-user-intent", {
    headers: authentication(),
    params,
  });
};

export const addCard = (params) => {
  return client().post("/astra-add-card", {
    headers: authentication(),
    params,
  });
};

export const isInstantRefundAllowedForReturn = (params: {
  country: string;
  manualReview: boolean;
  orderId: string;
}) => {
  return client().post("/instant-refund/validation/return", {
    headers: authentication(),
    params,
  });
};

export const isInstantRefundAllowedForCustomer = (params: {
  country: string;
  manualReview: boolean;
  orderId: string;
  astraUserId: string;
  amount: number;
}) => {
  return client().post("/instant-refund/validation/customer", {
    headers: authentication(),
    params,
  });
};
